import { useEffect, useRef } from 'react'
import { Screen, PrimaryButton } from './Screen.jsx'
import { ErrorBoundary, CrashHeading } from './ErrorBoundary.jsx'
import { currentBundle, screensOf } from '../config.js'

/**
 * The last boundary. Catches anything the battery boundary does not.
 *
 * WHY IT EXISTS SEPARATELY. BatteryBoundary covers the two answering surfaces,
 * where a crash costs a participant real work. A throw on Settings, Home or the
 * withdrawal screen still white-screened with nothing at all.
 *
 * THE WITHDRAWAL SCREEN IS THE CASE THAT DECIDES IT. She is being 403'd out as
 * it renders, so a blank page there is the LAST THING THE APP EVER SHOWS HER:
 * no way back, no account to return to, and no way to know whether she actually
 * left. Every other crash is recoverable by reopening. That one is not.
 *
 * WHAT IT MAY NOT SAY, and this is the whole reason it is its own component.
 * Decision #82 rejected a root boundary that claimed "your answers are saved",
 * because on Settings there are none — a claim wider than its trigger, which is
 * architecture.md's named defect class. It rejected the CLAIM, not the
 * boundary. So this asserts nothing it cannot know: nothing about answers,
 * nothing about what was saved, nothing about where she will land. It shares
 * the mechanism with BatteryBoundary and none of its words.
 *
 * NESTING IS THE POINT. React gives a throw to the INNERMOST boundary, so a
 * crash inside the battery is still caught by BatteryBoundary and still gets
 * the sentence about saved answers. This one only ever sees what that one did
 * not, which is exactly the surfaces where the sentence would be false.
 */

/**
 * config's words now (CFG-30 screens.error_boundary.root, design DS-2 §3.11b).
 * My placeholder is gone.
 *
 * IT PROMISES NOTHING, and config's note says that IS the specification: it may
 * not say answers are saved, nor that nothing changed, nor that she will see
 * where she was — because after a withdrawal POST whose render threw, all three
 * are false. Same heading as the other two variants; they differ by truth,
 * never by tone.
 *
 * Inlined only as the cold-cache fallback, exactly as config states them. A
 * crash screen cannot afford to wait on a fetch that may be what crashed.
 */
const DEFAULTS = {
  heading: 'This screen stopped working.',
  body: 'You can open the app again.',
  action: 'Open the app',
}

function RootCrashScreen() {
  const heading = useRef(null)
  useEffect(() => {
    heading.current?.focus()
  }, [])

  const copy = screensOf(currentBundle())?.error_boundary?.root ?? DEFAULTS

  return (
    /*
      The support affordance renders here too. A crash caught at the root can
      have happened anywhere, including on or beside a safety surface, and a
      boundary that removed the one permanent route to crisis resources would
      be worst exactly when it fired. It is an ordinary <a href>, so it survives
      a router that is part of what broke.
    */
    <Screen support>
      <CrashHeading headingRef={heading}>{copy.heading ?? DEFAULTS.heading}</CrashHeading>
      <p className="text-base leading-relaxed text-[var(--ink-900)]">{copy.body ?? DEFAULTS.body}</p>
      <div className="mt-8">
        {/* Never on mount. An automatic reload of a crashing tree is a loop she
            cannot escape. */}
        <PrimaryButton onClick={() => window.location.reload()}>
          {copy.action ?? DEFAULTS.action}
        </PrimaryButton>
      </div>
    </Screen>
  )
}

export function RootBoundary({ children }) {
  return (
    <ErrorBoundary label="root" fallback={<RootCrashScreen />}>
      {children}
    </ErrorBoundary>
  )
}
